import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { AlertCircle, Loader2 } from "lucide-react";

const LOGO_URL = "https://customer-assets-wrfwihn1.emergentagent.net/job_park-light-manager/artifacts/ljez3ua3_Senza%20titolo88.png";

/**
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 *
 * AuthCallback handles the redirect from Emergent OAuth. It reads #session_id=...
 * from the URL fragment, exchanges it with the backend for a session cookie,
 * then navigates to /dashboard. Use useRef to guard against React StrictMode double-invoke.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const { refresh } = useAuth();
  const [error, setError] = useState("");

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;

    if (!sessionId) {
      setError("Session ID mancante nell'URL");
      return;
    }

    (async () => {
      try {
        await api.post("/auth/google/session", { session_id: sessionId });
        // Clear the fragment from the URL
        window.history.replaceState(null, "", window.location.pathname);
        await refresh();
        navigate("/dashboard", { replace: true });
      } catch (err) {
        const detail = err?.response?.data?.detail;
        setError(typeof detail === "string" ? detail : "Autenticazione fallita");
        // eslint-disable-next-line no-console
        console.error("[Irollo CRM] Google auth callback failed:", err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen w-full bg-[#1C1917] flex flex-col items-center justify-center px-6 py-12">
      <div className="w-full max-w-md text-center">
        <img
          src={LOGO_URL}
          alt="Irollo"
          className="w-40 h-auto mx-auto mb-6"
          style={{ filter: "invert(1) brightness(1.15) contrast(1.05)" }}
        />
        {error ? (
          <div className="bg-red-950/40 border border-red-900/50 text-red-300 rounded-2xl p-6">
            <AlertCircle className="w-6 h-6 mx-auto mb-2" />
            <div className="font-medium mb-2">Accesso non riuscito</div>
            <div className="text-sm mb-4">{error}</div>
            <button
              onClick={() => navigate("/login", { replace: true })}
              className="text-[#F56A1F] hover:underline text-sm">
              Torna al login
            </button>
          </div>
        ) : (
          <div className="text-stone-400 flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Accesso in corso…
          </div>
        )}
      </div>
    </div>
  );
}
